import React, { ReactNode, useState } from 'react';

import { Box, Card, CardContent, CardHeader } from '@mui/material';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';
import { ChartGroupByLoadStatus } from '@cnx-saas/common/generated';
import { DepotLoadsPieChart } from '../../charts';
import { CardLabel2 } from './CardParts';

type Widget = {
  id: string;
  headerTitle: string;
  content: ReactNode;
};

export function LoadsWidgets() {
  const { t } = useTranslation();

  const storageId = 'loadsWidgetsOrder';

  const initialWidgets: Widget[] = [
    {
      id: 'unfinalized-loads',
      headerTitle: t('dashboard.loadCardHeader.preplannedLoads', {
        defaultValue: 'Loads in pre-planning',
      }),
      content: (
        <DepotLoadsPieChart
          groupByLoadStatus={ChartGroupByLoadStatus.PrePlanned}
        />
      ),
    },
    {
      id: 'unplanned-loads',
      headerTitle: t('dashboard.loadCardHeader.unplannedLoads', {
        defaultValue: 'Unplanned loads',
      }),
      content: (
        <DepotLoadsPieChart
          groupByLoadStatus={ChartGroupByLoadStatus.Unplanned}
        />
      ),
    },
  ];

  const getInitialWidgets = (): Widget[] => {
    const savedOrder = localStorage.getItem(storageId);
    if (savedOrder) {
      const savedWidgetOrder = JSON.parse(savedOrder);
      return savedWidgetOrder
        .map((widgetId: string) =>
          initialWidgets.find((widget) => widget.id === widgetId),
        )
        .filter(Boolean);
    }
    return initialWidgets;
  };
  const [widgets, setWidgets] = useState(getInitialWidgets());

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const items = Array.from(widgets);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setWidgets(items);

    const widgetOrder = items.map((widget) => widget.id);
    localStorage.setItem(storageId, JSON.stringify(widgetOrder));
  };

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable droppableId="loads" direction="horizontal">
        {(provided) => (
          <Box
            ref={provided.innerRef}
            {...provided.droppableProps}
            sx={{ display: 'flex', gap: 2 }}
          >
            {widgets.map((widget, index) => (
              <Draggable key={widget.id} draggableId={widget.id} index={index}>
                {(provided) => (
                  <Card
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    sx={{ flex: 1, height: '100%', borderRadius: 2 }}
                  >
                    <CardHeader
                      title={
                        <CardLabel2
                          label={widget.headerTitle}
                          dragHandleProps={provided.dragHandleProps}
                        />
                      }
                      sx={{
                        backgroundColor: 'white',
                        padding: '8px 16px',
                      }}
                    />
                    <CardContent
                      sx={(theme) => ({
                        background: theme.palette.success.light,
                        padding: 0,
                        '&:last-child': {
                          paddingBottom: 0,
                        },
                      })}
                    >
                      {widget.content}
                    </CardContent>
                  </Card>
                )}
              </Draggable>
            ))}
          </Box>
        )}
      </Droppable>
    </DragDropContext>
  );
}
